import type { ICameraVideoTrack, IMicrophoneAudioTrack } from 'agora-rtc-react';
import AgoraRTC from 'agora-rtc-react';

import { StudioLogger } from '@studio/logger';

const LOG = new StudioLogger('agoraDeviceListeners');

/* eslint-disable no-unused-vars */
interface DispatchFunction {
  (action: any): void;
}

/* eslint-disable no-unused-vars */
interface Actions {
  handleVideoInputChanged: (...args: any[]) => any;
  handleAudioInputChanged: (...args: any[]) => any;
  showNotification: (...args: any[]) => any;
  addToAttendeeTiles: (...args: any[]) => any;
}

function setupDeviceListeners(
  localAudioTrack: IMicrophoneAudioTrack | null,
  localCameraTrack: ICameraVideoTrack | null,
  dispatch: DispatchFunction,
  actions: Actions,
  attendeeId: string
) {
  if (localCameraTrack) {
    AgoraRTC.on('camera-changed', async (changedDevice: any) => {
      const switchDevice = await handleDeviceChange(
        'video',
        localCameraTrack,
        changedDevice,
        dispatch,
        actions.handleVideoInputChanged,
        actions.showNotification
      );
      //When we change the camera device, we need to update the tile so that the local video feed re-renders
      if (switchDevice) {
        dispatch(actions.addToAttendeeTiles(attendeeId, localCameraTrack));
      }
    });
  }

  if (localAudioTrack) {
    AgoraRTC.on('microphone-changed', async (changedDevice: any) => {
      await handleDeviceChange(
        'audio',
        localAudioTrack,
        changedDevice,
        dispatch,
        actions.handleAudioInputChanged,
        actions.showNotification
      );
    });
  }
}

function setupPlaybackDeviceListener() {
  AgoraRTC.on('playback-device-changed', (info: any) => {
    LOG.info(`Playback device changed: ${JSON.stringify(info)}`);
  });
}

async function handleDeviceChange(
  deviceType: 'audio' | 'video',
  deviceTrack: IMicrophoneAudioTrack | ICameraVideoTrack,
  changedDevice: any,
  dispatch: DispatchFunction,
  handleDevice: Function,
  showNotification: Function
) {
  LOG.info(`${deviceType} device changed: ${JSON.stringify(changedDevice)}`);
  let switchDevice = false;
  const deviceNaming = deviceType === 'audio' ? 'microphone' : 'camera';
  // When a new device is detected, switch to the new device
  if (changedDevice.state === 'ACTIVE') {
    try {
      await deviceTrack.setDevice(changedDevice.device.deviceId);
      dispatch(handleDevice(changedDevice.device.deviceId));
      switchDevice = true;
    } catch (error) {
      LOG.error(`Error switching to new ${deviceNaming} device: ${error}`);
    }
  } else if (changedDevice.device.label === deviceTrack.getTrackLabel()) {
    let oldDevices;

    if (deviceType === 'audio') {
      oldDevices = await AgoraRTC.getMicrophones();
    } else {
      oldDevices = await AgoraRTC.getCameras();
    }

    // Switch to an existing device when the unplugged device is the current device
    if (oldDevices[0]) {
      await deviceTrack.setDevice(oldDevices[0].deviceId);
      dispatch(handleDevice(oldDevices[0]?.deviceId));
      switchDevice = true;

      LOG.info(`${deviceNaming} ${changedDevice.device.label} disconnected. Just changed to default.`);
      dispatch(
        showNotification(`Your ${deviceNaming} ${changedDevice.device.label} disconnected. Just changed to default.`, {
          timeout: 5000,
          type: 'alert'
        })
      );
    }
  }
  return switchDevice;
}

export { setupDeviceListeners, setupPlaybackDeviceListener, handleDeviceChange };
